import React, { memo, useCallback, useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import Icon from "../../assets/icons/Icon";
import logo from "/assets/images/logo.png";
import { useSelector } from "react-redux";
import { dispatch } from "../../store";
import { uiActions } from "../../store/ui/ui-slice";

const findOpenPath = (routes = [], pathname) => {
  for (const route of routes) {
    const hasChildren = route.children?.length > 0;

    if (route.formPath === pathname) {
      return [];
    }

    if (hasChildren) {
      const childPath = findOpenPath(route.children, pathname);

      if (childPath) {
        return [route.formId, ...childPath];
      }
    }
  }

  return null;
};

const Sidebar = () => {
  const menu = useSelector((state) => state.user.formList) || [];
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  // Track one open submenu per depth level.
  const [openMenus, setOpenMenus] = useState({});

  useEffect(() => {
    const activePath = findOpenPath(menu, location.pathname);

    if (!activePath?.length) {
      return;
    }

    setOpenMenus(
      activePath.reduce((acc, formId, depth) => {
        acc[depth] = formId;
        return acc;
      }, {}),
    );
  }, [location.pathname, menu]);

  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth <= 992;

      document.body.setAttribute(
        "data-sidebar-size",
        isMobile ? "collapsed" : "default",
      );

      setCollapsed(isMobile);
    };

    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleMenu = useCallback((id, depth) => {
    setOpenMenus((prev) => {
      const nextState = Object.entries(prev).reduce((acc, [key, value]) => {
        if (Number(key) < depth) {
          acc[key] = value;
        }
        return acc;
      }, {});

      if (prev[depth] === id) {
        return nextState;
      }

      nextState[depth] = id;
      return nextState;
    });
  }, []);

  // Toggle sidebar size
  const toggleSidebarSize = useCallback((isTriggeredByNavLink = false) => {
    const body = document.body;
    const isMobileView = window.innerWidth < 992;
    const currentSize = body.getAttribute("data-sidebar-size");

    // 📱 Mobile behavior
    if (isMobileView) {
      if (isTriggeredByNavLink) {
        body.setAttribute("data-sidebar-size", "collapsed");
        setCollapsed(true);
      } else {
        const newSize = currentSize === "default" ? "collapsed" : "default";
        body.setAttribute("data-sidebar-size", newSize);
        setCollapsed(newSize === "collapsed");
      }
      return;
    }

    //  Desktop behavior
    if (isTriggeredByNavLink) {
      body.setAttribute("data-sidebar-size", "default");
      setCollapsed(false);
      return;
    }

    const newSize = currentSize === "default" ? "collapsed" : "default";
    body.setAttribute("data-sidebar-size", newSize);
    setCollapsed(newSize === "collapsed");
  }, []);

  // Render menu items recursively
  const renderMenu = useCallback(
    (routes, depth = 0) => {
      if (!routes || routes.length === 0) return null;

      return routes.map((route) => {
        const hasChildren = route.children?.length > 0;
        const isOpen = openMenus[depth] === route.formId;

        // PARENT MENU (with children)
        if (hasChildren) {
          return (
            <li key={route.formId} className="nav-item">
              <div
                className={`nav-link parent arrow_icon ${isOpen ? "active" : ""}`}
                onClick={() => toggleMenu(route.formId, depth)}
                role="button"
                tabIndex={0}
                aria-expanded={isOpen}
                onKeyDown={(e) =>
                  e.key === "Enter" && toggleMenu(route.formId, depth)
                }
              >
                <Icon name={route.formIcon} className="menu-icon" />
                <span>{route.formName}</span>
              </div>

              {/* COLLAPSIBLE CHILDREN */}
              <ul
                className={`collapse nav flex-column submenu ${isOpen ? "show" : ""}`}
              >
                {renderMenu(route.children, depth + 1)}
              </ul>
            </li>
          );
        }

        // SINGLE LINK
        return (
          <li key={route.formId} className="nav-item">
            <NavLink
              className={({ isActive }) =>
                `nav-link ${isActive ? "active" : ""}`
              }
              to={route.formPath || "#"}
              onClick={() => {
                toggleSidebarSize(true);
                dispatch(uiActions.setSubHeadingDetails(route));
              }}
            >
              <Icon name={route.formIcon} className="menu-icon" />
              <span>{route.formName}</span>
            </NavLink>
          </li>
        );
      });
    },
    [openMenus, toggleMenu, toggleSidebarSize],
  );

  return (
    <>
      <aside className="sidebar-cover">
        {/* LOGO SECTION */}
        <div className="aside-logo">
          <NavLink to="/" className="logo_box">
            <img src={logo} className="logo_img" alt="logo" />
          </NavLink>

          <button
            className="aside-toggle"
            onClick={() => toggleSidebarSize(false)}
            aria-label="Toggle sidebar"
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <span className={collapsed ? "" : "rotate-180"}>
              <Icon name="ChevronRight" />
            </span>
          </button>
        </div>

        {/* MENU SECTION */}
        <div className="aside-menu">
          <div className="startbar-menu">
            <div className="startbar-collapse">
              <ul className="navbar-nav mb-auto w-100">
                <li className="menu-label">
                  <span>Master</span>
                </li>
                {renderMenu(menu)}
              </ul>
            </div>
          </div>
        </div>
      </aside>

      {/* Sidebar Background Overlay (Mobile) */}
      <div
        className="sidebarBg_color"
        onClick={() => toggleSidebarSize(true)}
        role="button"
        tabIndex={-1}
      />
    </>
  );
};

export default memo(Sidebar);
